// 2. Third-party library imports
import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';
import { get, toLower } from 'lodash-es';

// 3. Local imports
// 3.1 Constants
import { REQUEST_PAYMENTPLAN_PAGE } from 'src/config/tableAndPageConstants';
import { API_NAME } from 'src/config/config';
// 3.4 Utility functions
import client, { post } from 'src/api';
import { graphqlCallWithRetry } from 'src/utils/sagaUtilsFunctions';
import {
    getNewHeaderKeyForCustomerPortal,
    getSortFieldsWithCustomFields,
    removeAppliedFiltersForApiRequest,
} from 'src/utils/commonFunctions';

// 3.5 Redux actions
import {
    getCustomerPortalDataAction,
    getCustomerPortalDataFailureAction,
    getCustomerPortalDataSuccessAction,
    getCustomerPortalRequestPaymentPlanDataAction,
    getCustomerPortalRequestPaymentPlanDataFailureAction,
    getCustomerPortalRequestPaymentPlanDataSuccessAction,
} from './customerPortalSlice';
import { getCustomerUILabel, getSelectedCompanyId } from '../companies/companySlice';

// 3.6 GraphQL queries
import { _queries as queries } from '../../graphql/queries.graphql';

// 3.7 Types
import { Invoice } from '../invoices/types';
import { PaymentPlan } from '../paymentPlans/types';
import { DynamicObject } from 'src/utils/commonInterfaces';

let refetchCount = 0;

function* handleGetCustomerPortalRequest({ payload }: any) {
	const {
		customerId,
        isUsingCloudImportType,
        callback,
	} = payload;
	const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(
        customerLabel
    )} details. Please try again later.`;

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_DETAILS_FOR_COMPANY,
                variables: {
                    CustomerId: customerId,
                    IsCloudImportType: isUsingCloudImportType,
                }
            },
            getNewHeaderKeyForCustomerPortal(customerId)
        );

        const Customer = get(res.data, 'GetCustomerDetailsForCompany');

        if (!Customer) {
            yield put(getCustomerPortalDataFailureAction([errorMessage]));
            return;
        }

        yield put(getCustomerPortalDataSuccessAction(Customer));
		
		if (callback) callback();
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerPortalDataFailureAction([errorMessage]));
    }
};

function* handleGetCustomerPortalRequestPaymentPlanAction({ payload }: any) {
    const errorMessage = 'Error fetching payment plan details. Please try again later.';

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
            isPaymentPlanEnabled,
            excludeInvoices
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice',
            true
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        
        const { CustomerId } = cleanFilters;
        let currentPage = 0;
        let pageSize = REQUEST_PAYMENTPLAN_PAGE.pageSize;
        let invoices: Invoice[] = [];
        let hasNextPage = true;
        do {
            const invoicesPartial: DynamicObject = yield call(
                graphqlCallWithRetry,
                queries.GET_INVOICES_FOR_COMPANY,
                {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * pageSize,
                    IsCloudImportType: isUsingCloudImportType,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                    UsingCustomerWorkflow: usingCustomerWorkflow,
                    PaymentPlanEnabled: isPaymentPlanEnabled,
                    ExcludeInvoices: excludeInvoices
                },
                'data.GetInvoicesForCompany',
                CustomerId
            );

            const { Invoices } = invoicesPartial;
            if (!Invoices || Invoices.length === 0) {
                break;
            }

            invoices.push(...Invoices);
            currentPage++;
            hasNextPage = !(Invoices.length < pageSize) && !(pageSize < REQUEST_PAYMENTPLAN_PAGE.pageSize);

        } while (hasNextPage);

        const invoiceIds = invoices.map((invoice: Invoice) => invoice.Id);
        const companyId: string | undefined = yield select(getSelectedCompanyId);
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/assignconfig',
            options: {
                body: {
                    InvoiceIds: invoiceIds,
                    CompanyId: companyId,
                    CreditIds: [],
                    CustomerId: CustomerId,
                }
            }
        });
        
        const paymentPlans: PaymentPlan[] = yield body.json();
        yield put(
            getCustomerPortalRequestPaymentPlanDataSuccessAction({
                invoices,
                paymentPlans
            })
        )

    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerPortalRequestPaymentPlanDataFailureAction([errorMessage]))
    }
}

function* watchGetCustomerPortalRequestPaymentPlanAction() {
    yield takeLatest(
        getCustomerPortalRequestPaymentPlanDataAction.type,
        handleGetCustomerPortalRequestPaymentPlanAction
    );
}

function* watchGetCustomerPortalRequest() {
	yield takeLatest(
		getCustomerPortalDataAction.type,
		handleGetCustomerPortalRequest
	)
};

function* customerPortalSaga() {
	yield all([
		fork(watchGetCustomerPortalRequest),
        fork(watchGetCustomerPortalRequestPaymentPlanAction)
	]);
};

export default customerPortalSaga;